import { apiHandler } from "../buyerApiHandler";

/**
 * Buyer home top-dash tabs (shown after the fixed "Food" tab), with the
 * restaurants and dishes each tab lists. Buyer API /admin/home-tabs.
 */

export type HomeTab = {
  id: number;
  title: string;
  image_url: string | null;
  status: boolean;
  sequence: number;
  restaurant_count?: number;
  dish_count?: number;
};

export type HomeTabEntry = {
  id: number;
  tab_id: number;
  entity_type: "store" | "item";
  entity_id: number;
  sequence: number;
  name: string | null;
  store_name: string | null;
  missing: boolean;
};

export type PickOption = { id: number; name: string; store_name?: string | null };

const json = { headers: { "Content-Type": "application/json" } };
const multipart = { headers: { "Content-Type": "multipart/form-data" } };

// The Buyer API wraps bodies in { success, data }.
const unwrap = (res: any) => res?.data ?? res;

export const listHomeTabs = async (): Promise<{
  food_image_url: string | null;
  food_title: string;
  max_tabs: number;
  shape: { points: [number, number][]; roundness: number } | null;
  tabs: HomeTab[];
}> => {
  const body = unwrap(await apiHandler("get", "/admin/home-tabs"));
  return {
    food_image_url: body?.food_image_url ?? null,
    food_title: body?.food_title || "Food",
    max_tabs: Number(body?.max_tabs) || 3,
    shape: body?.shape ?? null,
    tabs: body?.tabs ?? [],
  };
};

/** Heading of the home categories row ("What's on your mind?"). */
export const getWoymTitle = async (): Promise<string> => {
  const body = unwrap(await apiHandler("get", "/admin/home-tabs/woym-title"));
  return body?.woym_title || "What's on your mind?";
};

export const updateWoymTitle = (title: string) =>
  apiHandler("patch", "/admin/home-tabs/woym-title", { title }, json);

/** Saves the tab card shape; open buyer apps redraw within seconds. */
export const updateTabShape = (shape: { points: [number, number][]; roundness: number }) =>
  apiHandler("patch", "/admin/home-tabs/shape", shape, json);

export const updateFoodTabTitle = (title: string) =>
  apiHandler("patch", "/admin/home-tabs/food", { title }, json);

export const createHomeTab = async (title: string): Promise<HomeTab> =>
  unwrap(await apiHandler("post", "/admin/home-tabs", { title }, json));

export const updateHomeTab = async (
  id: number,
  payload: Partial<Pick<HomeTab, "title" | "status">> & { image_url?: string },
): Promise<HomeTab> =>
  unwrap(await apiHandler("patch", `/admin/home-tabs/${id}`, payload, json));

export const deleteHomeTab = (id: number) =>
  apiHandler("delete", `/admin/home-tabs/${id}`);

export const reorderHomeTabs = (tab_ids: number[]) =>
  apiHandler("patch", "/admin/home-tabs/reorder", { tab_ids }, json);

export const uploadHomeTabImage = async (id: number, file: File): Promise<HomeTab> => {
  const fd = new FormData();
  fd.append("image", file);
  return unwrap(await apiHandler("post", `/admin/home-tabs/${id}/image`, fd, multipart));
};

export const uploadFoodTabImage = async (file: File): Promise<string | null> => {
  const fd = new FormData();
  fd.append("image", file);
  const body = unwrap(await apiHandler("post", "/admin/home-tabs/food/image", fd, multipart));
  return body?.food_image_url ?? null;
};

export const getHomeTabEntries = async (id: number): Promise<HomeTabEntry[]> =>
  unwrap(await apiHandler("get", `/admin/home-tabs/${id}/entries`)) ?? [];

export const addHomeTabEntries = async (
  id: number,
  entity_type: "store" | "item",
  entity_ids: number[],
): Promise<HomeTabEntry[]> =>
  unwrap(
    await apiHandler(
      "post",
      `/admin/home-tabs/${id}/entries`,
      { entity_type, entity_ids },
      json,
    ),
  ) ?? [];

export const removeHomeTabEntry = (id: number, entryId: number) =>
  apiHandler("delete", `/admin/home-tabs/${id}/entries/${entryId}`);

export const reorderHomeTabEntries = async (
  id: number,
  entries: Array<{ entry_id: number; sequence: number }>,
): Promise<HomeTabEntry[]> =>
  unwrap(
    await apiHandler("patch", `/admin/home-tabs/${id}/entries/reorder`, { entries }, json),
  ) ?? [];

/** Restaurant / dish search, reusing the collection pickers. */
export const searchStores = async (search: string): Promise<PickOption[]> => {
  const body = unwrap(
    await apiHandler("get", "/collection/options/stores", undefined, {
      params: { search, limit: 30, page: 1 },
    }),
  );
  return body?.data ?? body ?? [];
};

export const searchItems = async (search: string): Promise<PickOption[]> => {
  const body = unwrap(
    await apiHandler("get", "/collection/options/items", undefined, {
      params: { search, limit: 30, page: 1 },
    }),
  );
  return body?.data ?? body ?? [];
};
